import { type MondayClient, AbortedError } from './monday-api/common';
import { getFirstBoardItemsPage, getBoardItems, getNextBoardItems, type Metric } from './monday-api/api';

/*
 {
  "type": "error",
  "errorType": "runtime",
  "errorText": "{\"errors\":[{\"message\":\"Internal server error\",\"path\":[],\"extensions\":{\"status_code\":500,\"error_code\":\"INTERNAL_SERVER_ERROR\"}}],\"extensions\":{\"request_id\":\"64015243-b05b-915b-a8b1-92b1d789631f\"},\"status_code\":500,\"error_message\":\"Internal server error\",\"account_id\":17155708}",
  "statusCode": 500,
  "graphqlQuery": "\n    query getNextBoardItems($cursor: String!, $limit: Int!) {\n      complexity {\n        query\n      }\n      next_items_page(cursor: $cursor, limit: $limit) {\n        cursor\n        items {\n          \n    id\n    created_at\n    creator_id\n    name\n    state\n    updated_at\n    email\n    relative_link\n    group {\n      id\n      title\n    }\n    parent_item {\n      id\n    }\n    column_values {\n      id\n      text\n      type\n      value\n      ... on BoardRelationValue { display_value }\n      ... on MirrorValue { display_value }\n      ... on DependencyValue {\n        display_value\n      }\n      ... on TagsValue {\n        text\n      }\n    }\n  \n          \n        }\n      }\n    }\n  ",
  "graphqlVariables": {
    "cursor": "XYZ",
    "limit": 10
  },
  "scope": "DEFAULT"
}
*/

type NarrowDownFailingItemOptions = {
  cursor?: string;
  boardId: number;
  maxLimit: number;
  queryParams?: string;
  abortSignal?: AbortSignal;
};

export async function narrowDownFailingItem(mondayClient: MondayClient, logger: Logger, options: NarrowDownFailingItemOptions) {
  async function fetchItems(i: number, withoutMirrorColumns = false) {
    if (options.cursor) {
      await getNextBoardItems(mondayClient, options.cursor, i, false, {
        withoutBoardRelationValue: withoutMirrorColumns,
        withoutMirrorValue: withoutMirrorColumns,
      });
    }

    const result = await getFirstBoardItemsPage(mondayClient, options.boardId, i, options.queryParams, false, {
      withoutBoardRelationValue: withoutMirrorColumns,
      withoutMirrorValue: withoutMirrorColumns,
    });

    return result.items;
  }

  for (let i = 1; i <= options.maxLimit; i++) {
    try {
      logger(`Testing with ${i} items per page`, 'info');
      await fetchItems(i);

      if (options.abortSignal?.aborted) {
        throw new AbortedError('Aborted narrowing down failing item');
      }
    } catch (err) {
      if (err instanceof AbortedError) {
        logger('Narrowing down failing item was aborted', 'info');
        return;
      }

      logger(`Error happened while testing ${i} items per page: ${(err as any)?.message || 'Unknown error'}`, 'info');

      try {
        const resultItems = await fetchItems(i, true);

        if (resultItems.length === 0) {
          logger(`No items found with ${i} items per page`, 'info');
          return;
        }

        const lastItem = resultItems[resultItems.length - 1];
        logger(`Successfully retrieved items (first item). ID: ${lastItem.id}, Name: ${lastItem.name}`, 'info');

        return lastItem;
      } catch (err) {
        logger(
          `Error happened while testing ${i} items per page without mirror columns: ${(err as any)?.message || 'Unknown error'}`,
          'info',
        );
        return;
      }
    }
  }
}

type Logger = (message: string, type: 'info' | 'warning' | 'error') => void;

type DiagnoseResult = {
  type: 'success' | 'error';
  duration: number;
  testId: string;
  metrics: Record<string, number>;
  message: string;
  report: any;
};

type RunOptions = {
  boardId: number;
  abortSignal?: AbortSignal;
};

export type DiagnoseTest = {
  id: string;
  name: string;
  description: string;
  run: (mondayClient: MondayClient, options: RunOptions, logger: Logger) => Promise<DiagnoseResult>;
};

function clone(data: any) {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (err) {
    console.error('Failed to clone data', err);
    return null;
  }
}

function createErrorJson(error: any) {
  return {
    message: error.message,
    stack: error.stack,
    name: error.name,
    requesterErrorResult: error?.requesterErrorResult,
  };
}

type RunBoardDiagnosisOptions = {
  boardId: number;
  testId: string;
  itemsPerPage: number;
  withMirrorColumns: boolean;
  withSubitems: boolean;
  queryParams?: string;
  abortSignal?: AbortSignal;
};

async function runBoardDiagnosis(mondayClient: MondayClient, logger: Logger, options: RunBoardDiagnosisOptions): Promise<DiagnoseResult> {
  const log: { message: string; type: string; timestamp: number }[] = [];
  const metrics: Metric[] = [];
  const metricsSummary: { [key: string]: number } = {};
  let itemsResult = null;
  let error = null;
  const startTime = Date.now();

  try {
    itemsResult = await getBoardItems(mondayClient, options.boardId, {
      logger: (message, type) => {
        log.push({ message, type, timestamp: Date.now() });
        logger(message, type);
      },
      onMetric: (metric) => {
        metrics.push(metric);
      },
      itemsPerPage: options.itemsPerPage,
      withSubitems: options.withSubitems,
      queryParams: options.queryParams,
      abortSignal: options.abortSignal,
      baseBoardFieldsOptions: {
        withoutBoardRelationValue: !options.withMirrorColumns,
        withoutMirrorValue: !options.withMirrorColumns,
        withoutSubitemsBoardRelationValue: !options.withMirrorColumns,
        withoutSubitemsMirrorValue: !options.withMirrorColumns,
      },
    });
  } catch (err) {
    error = err;
  }

  const duration = Date.now() - startTime;
  const metricsSummaryAvg = metrics.reduce(
    (acc, metric) => {
      if (!acc[metric.name]) {
        acc[metric.name] = { sum: 0, count: 0 };
      }
      acc[metric.name].sum += metric.value;
      acc[metric.name].count += 1;
      return acc;
    },
    {} as Record<string, { sum: number; count: number }>,
  );
  for (const metricName in metricsSummaryAvg) {
    metricsSummary[metricName] = metricsSummaryAvg[metricName].sum / metricsSummaryAvg[metricName].count;
  }
  const report = {
    boardId: options.boardId,
    log,
    metricsSummary,
    itemsCount: itemsResult?.length || 0,
    error: error ? createErrorJson(error) : null,
    errorQueryHistory: clone(mondayClient?.getErrorQueryHistory()),
  };

  const pagesLoadTimes = [];

  if (metricsSummary['getFirstResult']) {
    pagesLoadTimes.push(metricsSummary['getFirstResult']);
  }

  if (metricsSummary['getNextBoardItemsWithRetries']) {
    pagesLoadTimes.push(metricsSummary['getNextBoardItemsWithRetries']);
  }

  const averagePageLoadTime = pagesLoadTimes.length > 0 ? pagesLoadTimes.reduce((a, b) => a + b, 0) / pagesLoadTimes.length : 0;
  const averagePageLoadTimeFormatted = averagePageLoadTime ? (averagePageLoadTime / 1000).toFixed(2) : 0;

  return {
    type: error ? 'error' : 'success',
    duration,
    testId: options.testId,
    metrics: metricsSummary,
    // use the human readable message here?
    message: error
      ? `Error: ${(error as any)?.message || 'Unknown error'}`
      : `Success: ${itemsResult?.length} items retrieved. Average page load time: ${averagePageLoadTimeFormatted} seconds.`,
    report,
  };
}

const CommonBoardTest250: DiagnoseTest = {
  id: 'common-board-250',
  name: 'Common Board Test (250 items per page / with mirror columns)',
  description: 'Check if the common board is accessible and has items.',
  run: async (mondayClient, options, logger) => {
    return await runBoardDiagnosis(mondayClient, logger, {
      testId: 'common-board-250',
      boardId: options.boardId,
      itemsPerPage: 250,
      withMirrorColumns: true,
      withSubitems: false,
      abortSignal: options.abortSignal,
    });
  },
};

const CommonBoardTest50: DiagnoseTest = {
  id: 'common-board-50',
  name: 'Common Board Test (50 items per page / with mirror columns)',
  description: 'Check if the common board is accessible and has items.',
  run: async (mondayClient, options, logger) => {
    return await runBoardDiagnosis(mondayClient, logger, {
      testId: 'common-board-50',
      boardId: options.boardId,
      itemsPerPage: 50,
      withMirrorColumns: true,
      withSubitems: false,
      abortSignal: options.abortSignal,
    });
  },
};

const CommonBoardTest50WithoutMirror: DiagnoseTest = {
  id: 'common-board-50-without-mirror',
  name: 'Common Board Test (50 items per page / without mirror columns)',
  description: 'Check if the common board is accessible and has items.',
  run: async (mondayClient, options, logger) => {
    return await runBoardDiagnosis(mondayClient, logger, {
      testId: 'common-board-50-without-mirror',
      boardId: options.boardId,
      itemsPerPage: 50,
      withMirrorColumns: false,
      withSubitems: false,
      abortSignal: options.abortSignal,
    });
  },
};

const NarrowDownTest: DiagnoseTest = {
  id: 'narrow-down',
  name: 'Narrow Down Faulty Item',
  description: 'Test if there is a faulty item in the board.',
  run: async (mondayClient, options, logger) => {
    const result = await runBoardDiagnosis(mondayClient, logger, {
      testId: 'narrow-down',
      boardId: options.boardId,
      itemsPerPage: 50,
      withMirrorColumns: true,
      withSubitems: false,
      abortSignal: options.abortSignal,
    });

    if (result.type === 'error') {
      const graphqlQuery = result.report.error?.requesterErrorResult?.graphqlQuery;
      const graphqlVariables = result.report.error?.requesterErrorResult?.graphqlVariables;

      if (graphqlQuery && (graphqlQuery.includes('getFirstBoardItems') || graphqlQuery.includes('getNextBoardItems'))) {
        const maxLimit = 50;

        const failingItem = await narrowDownFailingItem(mondayClient, logger, {
          cursor: graphqlVariables?.cursor,
          boardId: options.boardId,
          abortSignal: options.abortSignal,
          maxLimit,
          queryParams: '',
        });

        if (failingItem) {
          result.message = `Found faulty item: ID: ${failingItem.id}, Name: ${failingItem.name}`;
        } else {
          result.message = 'There was a problem with the board, but no faulty item was found.';
        }
      }
    } else {
      result.message = 'Board is accessible and items were retrieved successfully.';
    }

    return result;
  },
};

export const AllTests: DiagnoseTest = {
  id: 'all-tests',
  name: 'All Tests',
  description: 'Run all common board tests.',
  run: async (mondayClient, options, logger) => {
    const results = [];
    for (const test of [CommonBoardTest250, CommonBoardTest50, CommonBoardTest50WithoutMirror, NarrowDownTest]) {
      const result = await test.run(mondayClient, options, logger);
      results.push(result);
    }

    return {
      type: 'success',
      testId: 'all-tests',
      duration: results.reduce((a, b) => a + b.duration, 0),
      metrics: {},
      message: 'All tests completed.',
      report: { results },
    };
  },
};

export const diagnoseTests: DiagnoseTest[] = [NarrowDownTest, CommonBoardTest250, CommonBoardTest50, CommonBoardTest50WithoutMirror];

export async function runDiagnoseTests(mondayClient: MondayClient, options: RunOptions, logger: Logger) {
  const results: DiagnoseResult[] = [];
  for (const test of diagnoseTests) {
    logger(`Running test: ${test.name}`, 'info');
    const result = await test.run(mondayClient, options, logger);

    logger(`Test result type: ${result.type}`, 'info');
    console.log('requesterErrorResult', JSON.stringify(result?.report?.error?.requesterErrorResult, null, 2));
    logger(`Test result: ${result.message}`, 'info');

    results.push(result);
  }
  return results;
}
